import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import SEOHead from "../section/SeoHead";
import RegisterHeroSection from "../section/RegisterHeroSection";
import WhyCreateAccount from "../section/WhyCreateAccount";
import RegistrationTypeTabs from "../section/RegistrationTypeTabs";
import UserRegistrationForm from "../section/UserRegistrationForm";
import OfficeRegistrationForm from "../section/OfficeRegistrationForm";
import PlansModal from "../section/PlansModal";
import registerAnimation from "../../assets/animations/register-animation.json";
import { structuredData } from "../../utils/utils";
import { useTranslation } from "react-i18next";

const Register = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialType = searchParams.get("type") || "user";
  const [registrationType, setRegistrationType] = useState(initialType);
  const [isPlansOpen, setIsPlansOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Register - Create Your Citizen Hub Account"
        description="Create a new Citizen Hub account to access our immigration services, manage your account information, and track your immigration cases online."
        keywords="register citizen hub, create immigration account, citizen hub sign up, immigration services registration"
        canonicalUrl="https://citizenhub.com/register"
        structuredData={structuredData}
      />

      <RegisterHeroSection />

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Registration Form */}
            <ScrollAnimation direction="left" delay={0.1}>
              <motion.div
                className="bg-white rounded-xl shadow-lg p-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t("auth.register.title")}
                  </h2>
                  <p className="text-gray-600">
                    {t("auth.register.subtitle")}
                  </p>
                </div>

                <RegistrationTypeTabs
                  type={registrationType}
                  onChange={setRegistrationType}
                />

                {registrationType === "user" ? (
                  <UserRegistrationForm />
                ) : (
                  <OfficeRegistrationForm
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                    openPlansModal={() => setIsPlansOpen(true)}
                  />
                )}

                <div className="mt-8 text-center">
                  <p className="text-gray-600">
                    {t("auth.register.already_account")}{" "}
                    <LanguageLink
                      to="/login"
                      className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                    >
                      {t("auth.register.sign_in")}
                    </LanguageLink>
                  </p>
                </div>
              </motion.div>
            </ScrollAnimation>

            {/* Right: Animation & Text */}
            <ScrollAnimation direction="right" delay={0.2}>
              <motion.div
                className="flex flex-col items-center justify-center"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <div className="w-full max-w-md">
                  <Lottie
                    animationData={registerAnimation}
                    loop
                    autoplay
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>
                <motion.div
                  className="text-center mt-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                >
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {t("auth.register.join_community")}
                  </h3>
                  <p className="text-gray-600">
                    {t("auth.register.join_text")}
                  </p>
                </motion.div>
              </motion.div>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      <WhyCreateAccount />

      {/* Plans Modal */}
      <PlansModal
        open={isPlansOpen}
        onClose={() => setIsPlansOpen(false)}
        selectedPlanId={selectedPlan?.id}
        onSelect={setSelectedPlan}
      />
    </div>
  );
};

export default Register;
